import {usePromotion} from '@/context/Promotion/PromotionContext'
import {Condition, CurrencyType, PromotionCondition} from '@/types/promotion'
import {COUNTRY_CODES} from '@/utils/constants/countryConstants'
import {ANY_BUY_OPTION, ANY_ID, PromotionContionsTabList} from '@/utils/constants/promotionConstants'
import {DropdownItem} from 'pepsico-ds'
import {useEffect, useMemo, useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import {useRewardValidations} from './Rewards/RewardValidations'

export type BuyTarget = 'main' | 'min'

// Maps a target ('main' buy condition vs. the optional 'min' quantity condition) to its Condition fields
const targetFields = {
  main: {quantity: 'quantity', itemType: 'itemType', items: 'items'},
  min: {quantity: 'minQuantity', itemType: 'minItemType', items: 'minItems'},
} as const

const toDropdownValue = (options: DropdownItem[] | undefined, id: unknown) =>
  options?.filter(item => item?.id === id) ?? []

const toProductsValue = (items: {id: string; name: string}[] = []) =>
  items.map(item => ({id: item.id, displayText: item.name}))

const getSelectedText = (items: {id: string; name: string}[] = []) => items.map(item => item.name).join(', ')

type UseBuySingleProductConditionArgs = {
  promotionCondition: PromotionCondition
  index: number
  selectedPromotionCondition: string
}

export const useBuySingleProductCondition = ({
  promotionCondition,
  index,
  selectedPromotionCondition,
}: UseBuySingleProductConditionArgs) => {
  const prevPromotionCondition = useRef(selectedPromotionCondition)
  const {updatePromotion, promotionConfig, isLoadingConfig} = usePromotion()
  const {hasBuyQuantityLayerError, hasMinQuantityError, hasMinQuantityLayerError} =
    useRewardValidations(promotionCondition)
  let buyTypeOptions = promotionConfig?.conditions?.itemTypeOptions ?? []
  const isAmountTab = selectedPromotionCondition === 'EA'
  const textAreavalue = isAmountTab
    ? (promotionCondition?.conditionGroup?.conditions?.[0]?.amount ??
      promotionCondition?.conditionGroup?.conditions?.[0]?.quantity)
    : promotionCondition?.conditionGroup?.conditions?.[0]?.quantity
  const [modalProps, setModalProps] = useState<{type: string | ''; show: boolean; target: BuyTarget}>({
    show: false,
    type: '',
    target: 'main',
  })

  const {countryCode} = useSelector((state: any) => state?.countryCode)
  const currencyCode = CurrencyType[countryCode ?? 'MX'] ?? '$'
  // Min quantity is currently only available for Turkey
  const isMinQuantityFeatureEnabled = countryCode === COUNTRY_CODES.TR

  const updateCurrentCondition = (update: Partial<Condition>) => {
    updatePromotion(prev => {
      const updatedConditions = prev.promotionConditions.map(condition => {
        if (condition.sequence !== promotionCondition.sequence) return condition

        const updatedConditionGroup = {
          ...condition.conditionGroup,
          conditions: (condition.conditionGroup?.conditions || []).map((item, idx) => {
            return idx === 0 ? {...item, ...update} : item
          }),
        }

        return {
          ...condition,
          conditionGroup: updatedConditionGroup,
        }
      })

      return {
        ...prev,
        promotionConditions: updatedConditions,
      }
    })
  }

  const handleBuyCount = (values: DropdownItem[]) => {
    const value = values?.[0]?.id
    if (value) {
      // If ANY is selected in the quantity dropdown, set quantity to 1
      if (isNaN(value) && value.toLowerCase() === ANY_ID.toLowerCase()) {
        updateCurrentCondition({quantity: 1})
      } else {
        updateCurrentCondition({quantity: Number(value)})
      }
    } else {
      updateCurrentCondition({quantity: null})
    }
  }

  const handleMinQuantity = (values: DropdownItem[]) => {
    const value = values?.[0]?.id
    updateCurrentCondition({minQuantity: value && !isNaN(value) ? Number(value) : null})
  }

  const handleToggleMinQuantity = (checked: boolean) => {
    // Applies across every layer since the toggle represents a single, promotion-wide setting
    updatePromotion((promotion: any) => ({
      ...promotion,
      promotionConditions: promotion?.promotionConditions?.map(condition => ({
        ...condition,
        conditionGroup: {
          ...condition.conditionGroup,
          conditions: condition?.conditionGroup?.conditions?.map((cond, idx) =>
            idx === 0 ? {...cond, hasMinQuantity: checked, minQuantity: checked ? cond.minQuantity : null} : cond
          ),
        },
      })),
    }))
  }

  const handleCloseModal = () => {
    setModalProps({type: '', show: false, target: 'main'})
  }

  // Applies the selected items/itemType either to the main or the min-quantity condition, across all layers
  const handleValues = (selectedItemsAndType, target: BuyTarget = 'main') => {
    const {values, itemType} = selectedItemsAndType || {}
    const fields = targetFields[target]

    if (!values || !Array.isArray(values)) {
      updateCurrentCondition({[fields.items]: [], [fields.itemType]: undefined})
      return
    }
    const items = values.map(item => ({
      id: item?.id,
      name: item?.displayText,
      uom: selectedPromotionCondition,
    }))
    const validIds = new Set(items.map(item => item.id))

    updatePromotion((promotion: any) => ({
      ...promotion,
      promotionConditions: promotion?.promotionConditions?.map(condition => ({
        ...condition,
        conditionGroup: {
          ...condition.conditionGroup,
          conditions: condition?.conditionGroup?.conditions?.map(cond => ({
            ...cond,
            [fields.itemType]: itemType,
            [fields.items]: items,
            // Min quantity products are a subset of the main products, so drop any that no longer exist
            ...(target === 'main' && {
              minItems: (cond.minItems ?? []).filter(minItem => validIds.has(minItem.id)),
            }),
          })),
        },
      })),
    }))
  }

  const handleSubmitModal = selectedItems => {
    handleValues(selectedItems, modalProps.target)
    handleCloseModal()
  }

  let buyQtyOptions = useMemo(
    () => promotionConfig?.buyQty?.map(value => ({displayText: value, id: Number(value)})),
    [promotionConfig?.buyQty]
  )

  const currentCondition = promotionCondition?.conditionGroup?.conditions?.[0]
  const hasMinQuantity = isMinQuantityFeatureEnabled && (currentCondition?.hasMinQuantity ?? false)

  const mainView = {
    items: currentCondition?.items ?? [],
    selectedText: getSelectedText(currentCondition?.items),
    productsValue: toProductsValue(currentCondition?.items),
    quantityValue: toDropdownValue(buyQtyOptions, currentCondition?.quantity),
  }

  const minView = {
    items: currentCondition?.minItems ?? [],
    selectedText: getSelectedText(currentCondition?.minItems),
    productsValue: toProductsValue(currentCondition?.minItems),
    quantityValue: toDropdownValue(buyQtyOptions, currentCondition?.minQuantity),
    textValue: currentCondition?.minQuantity != null ? String(currentCondition.minQuantity) : '',
  }

  // Selecting a main product either applies it directly or opens the selection modal for modal-backed types
  const handleSelectedBuyItem = (values: DropdownItem[]) => {
    if (!values || !values.length) {
      updateCurrentCondition({items: [], itemType: undefined})
      return
    }

    const isModalValue = values.find(item => buyTypeOptions?.find(({id}) => id === item?.id))

    let updatedItemType = currentCondition?.itemType
    let updatedValues = values

    if (isModalValue) {
      if (isModalValue?.id === ANY_ID) {
        updatedItemType = ANY_ID
        updatedValues = [{...ANY_BUY_OPTION, uom: PromotionContionsTabList[selectedPromotionCondition]?.uom} as any]
        handleValues({values: updatedValues, itemType: updatedItemType}, 'main')
      } else {
        setModalProps({show: true, type: isModalValue?.id, target: 'main'})
        return // stop execution if modal opens
      }
    } else {
      // For regular items
      handleValues({values, itemType: updatedItemType}, 'main')
    }
  }

  // Min quantity products are always a subset of the already-selected main products, so no modal is needed
  const handleSelectedMinBuyItem = (values: DropdownItem[]) => {
    if (!values || !values.length) {
      updateCurrentCondition({minItems: [], minItemType: undefined})
      return
    }

    handleValues({values, itemType: currentCondition?.itemType}, 'min')
  }

  const isAnySelected = currentCondition?.items?.find(item => item.id === ANY_ID)
  const buyQtyOptionsData = new Set(mainView.quantityValue?.map(item => item?.id))

  if (buyQtyOptionsData?.has('any')) {
    buyTypeOptions = buyTypeOptions?.filter(item => item?.id !== ANY_ID)
  }
  if (isAnySelected) {
    buyQtyOptions = buyQtyOptions?.filter(item => item?.id !== ANY_ID)
  }

  const isFirstCondition = promotionCondition?.sequence === 1
  const isError = hasBuyQuantityLayerError()
  const isMinQuantityError = hasMinQuantityError() || hasMinQuantityLayerError()
  const handleBuyAmount = (value: string) => {
    updateCurrentCondition({
      amount: value ? Number(value) : null,
    })
  }

  // minQuantity doubles as the min amount value on the amount tab, mirroring the transform's single field
  const handleMinAmount = (value: string) => {
    updateCurrentCondition({minQuantity: value ? Number(value) : null})
  }

  useEffect(() => {
    // Only run if selectedPromotionCondition actually changed
    if (prevPromotionCondition.current === selectedPromotionCondition) return

    // Update promotion with cleared quantity and updated UOM
    updatePromotion((promotion: any) => ({
      ...promotion,
      promotionConditions: promotion?.promotionConditions?.map(condition => ({
        ...condition,
        conditionGroup: {
          ...condition.conditionGroup,
          conditions: condition?.conditionGroup?.conditions?.map(cond => ({
            ...cond,
            items: cond?.items?.map(item => ({
              ...item,
              uom: selectedPromotionCondition,
            })),
          })),
        },
      })),
    }))
    prevPromotionCondition.current = selectedPromotionCondition
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPromotionCondition])

  const units = PromotionContionsTabList[0].uom
  const cases = PromotionContionsTabList[1].uom
  const isQuantityTab = selectedPromotionCondition === units || selectedPromotionCondition === cases

  return {
    index,
    isLoadingConfig,
    buyTypeOptions,
    buyQtyOptions,
    currentCondition,
    hasMinQuantity,
    isMinQuantityFeatureEnabled,
    mainView,
    minView,
    modalProps,
    isFirstCondition,
    isError,
    isMinQuantityError,
    isQuantityTab,
    currencyCode,
    textAreavalue,
    handleBuyCount,
    handleMinQuantity,
    handleToggleMinQuantity,
    handleBuyAmount,
    handleMinAmount,
    handleSelectedBuyItem,
    handleSelectedMinBuyItem,
    handleSubmitModal,
    handleCloseModal,
  }
}
